import { Grid } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import TransferControls from './TransferControls';
import SourceManagerColumn from './SourceManagerColumn';
import TargetManagerColumn from './TargetManagerColumn';
import { IOption } from '~/ui/components/inputs/Select/types';
import { IFormValues } from '../types';
import { IUserList } from '~/services/api/users/types';

interface IFormProps {
  sourceUser: IUserList;
  healthManagerOptions: IOption[];
  commonProgramIds: number[];
}

const PatientsReassignmentForm: FC<IFormProps> = ({
  sourceUser,
  healthManagerOptions,
  commonProgramIds,
}) => {
  const [selectedLeft, setSelectedLeft] = useState<number[]>([]);
  const [selectedRight, setSelectedRight] = useState<number[]>([]);

  const { watch, setValue } = useFormContext<IFormValues>();

  const [sourceUserId, targetUserId, targetPatients, sourcePatients] = watch([
    'sourceUserId',
    'targetUserId',
    'targetPatients',
    'sourcePatients',
  ]);

  useEffect(() => {
    setSelectedLeft([]);
    setSelectedRight([]);
  }, [targetUserId]);

  const duplicatedIds = useMemo(() => {
    const sourceIds = new Set(sourcePatients.map(p => p.id));
    return targetPatients.filter(p => sourceIds.has(p.id)).map(p => p.id);
  }, [sourcePatients, targetPatients]);

  const handleMoveRight = () => {
    const toMove = sourcePatients.filter(p => selectedLeft.includes(p.id));
    const remaining = sourcePatients.filter(p => !selectedLeft.includes(p.id));

    setValue('sourcePatients', remaining);
    setValue('targetPatients', [...targetPatients, ...toMove]);
    setSelectedLeft([]);
  };

  const handleMoveLeft = () => {
    const toMove = targetPatients.filter(p => selectedRight.includes(p.id));
    const remaining = targetPatients.filter(p => !selectedRight.includes(p.id));

    setValue('targetPatients', remaining);
    setValue('sourcePatients', [...sourcePatients, ...toMove]);
    setSelectedRight([]);
  };

  const filteredSourcePatients = useMemo(() => {
    if (!targetUserId) return sourcePatients;

    return sourcePatients.filter(p => commonProgramIds.includes(p.programId));
  }, [sourcePatients, commonProgramIds, targetUserId]);

  const filteredTargetPatients = useMemo(
    () => targetPatients.filter(p => commonProgramIds.includes(p.programId)),
    [targetPatients, commonProgramIds],
  );

  const isAssignAllowed = !!sourceUserId && !!targetUserId;

  const handleSetSelectedRight = (ids: number[]) => {
    if (isAssignAllowed) {
      setSelectedRight(ids);
    }
  };

  const handleSetSelectedLeft = (ids: number[]) => {
    if (isAssignAllowed) {
      setSelectedLeft(ids);
    }
  };

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={5.5}>
        <SourceManagerColumn
          sourceUser={sourceUser}
          disabledIds={duplicatedIds}
          selectedIds={selectedLeft}
          onSelect={handleSetSelectedLeft}
          patients={filteredSourcePatients}
        />
      </Grid>

      <Grid item xs={1}>
        <TransferControls
          onMoveRight={handleMoveRight}
          onMoveLeft={handleMoveLeft}
          disableRight={selectedLeft.length === 0 || !isAssignAllowed}
          disableLeft={selectedRight.length === 0 || !isAssignAllowed}
        />
      </Grid>

      <Grid item xs={5.5}>
        <TargetManagerColumn
          healthManagerOptions={healthManagerOptions}
          selectedIds={selectedRight}
          disabledIds={duplicatedIds}
          onSelect={handleSetSelectedRight}
          patients={filteredTargetPatients}
        />
      </Grid>
    </Grid>
  );
};

export default PatientsReassignmentForm;
